import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DailyWritingActivity } from '@/services/progress-tracker/api';
import { useYearlyActivity } from '@/services/query/progress-tracker-queries';
import { eachDayOfInterval, endOfMonth, endOfYear, format, getDay, startOfMonth, startOfYear } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface ActivityHeatmapProps {
  className?: string;
  projectId?: string;
}

export default function ActivityHeatmap({ className, projectId }: ActivityHeatmapProps) {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use TanStack Query to fetch activity data
  const { data: activityData = [], isLoading: loading } = useYearlyActivity(year, projectId);

  // Calculate max activity for color intensity scale
  const maxActivity = useMemo(() => {
    return activityData.reduce((max, day) => Math.max(max, day.totalMinutes), 0);
  }, [activityData]);

  // Scroll to end on mount or year change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [year, loading]);

  const previousYear = () => {
    setYear(year - 1);
  };

  const nextYear = () => {
    const currentYear = new Date().getFullYear();
    if (year < currentYear) {
      setYear(year + 1);
    }
  };



  // Create a map of dates to activity data for quick lookup
  const activityMap: Record<string, DailyWritingActivity> = {};
  activityData.forEach(day => {
    activityMap[day.date] = day;
  });

  // Calculate color intensity based on activity
  const getActivityColor = (minutes: number) => {
    if (minutes === 0) return 'bg-muted/20';
    const scale = maxActivity > 0 ? maxActivity : 60;
    const intensity = Math.min(Math.ceil((minutes / scale) * 4), 4);

    switch (intensity) {
      case 1: return 'bg-primary/20';
      case 2: return 'bg-primary/40';
      case 3: return 'bg-primary/60';
      case 4: return 'bg-primary/80';
      default: return 'bg-muted/20';
    }
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return hours > 0 ? `${hours}h ${mins} m` : `${mins} m`;
  };

  // Group days by month for discrete block rendering
  const months = useMemo(() => {
    const monthBlocks = [];
    for (let m = 0; m < 12; m++) {
      const monthStart = startOfMonth(new Date(year, m, 1));
      const monthEnd = endOfMonth(monthStart);
      const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

      // We render separate grids for each month
      // Grid is arrays of columns. 
      // Columns are arrays of 7 days (Sun-Sat).

      const grid: (Date | null)[][] = [];
      let currentColumn: (Date | null)[] = Array(7).fill(null);

      daysInMonth.forEach(day => {
        const dayOfWeek = getDay(day); // 0 (Sun) to 6 (Sat)
        currentColumn[dayOfWeek] = day;

        if (dayOfWeek === 6) { // Completed a week column
          grid.push(currentColumn);
          currentColumn = Array(7).fill(null);
        }
      });

      // Push partial last column if has data
      if (currentColumn.some(d => d !== null)) {
        grid.push(currentColumn);
      }

      monthBlocks.push({
        name: format(monthStart, 'MMMM'),
        grid
      });
    }
    return monthBlocks;
  }, [year]);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-4 flex-shrink-0">
        <div className="space-y-0.5">
          <h3 className="text-lg font-semibold text-foreground tracking-tight">
            Activity Calendar
          </h3>
          <div className="flex items-center gap-3 text-sm text-muted-foreground/80">
            <div className="flex items-baseline gap-1.5">
              <span className="font-bold text-foreground">{activityData.filter(d => d.totalMinutes > 0).length}</span>
              <span className="text-[11px] font-medium uppercase tracking-wide opacity-80">days active</span>
            </div>

            <span className="text-border/60 text-[10px]">•</span>

            <div className="flex items-baseline gap-1.5">
              <span className="font-bold text-foreground">{formatMinutes(activityData.reduce((acc, curr) => acc + curr.totalMinutes, 0))}</span>
              <span className="text-[11px] font-medium uppercase tracking-wide opacity-80">total time</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={previousYear}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-12 text-center">{year}</span>
          <Button
            onClick={nextYear}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            disabled={year >= new Date().getFullYear()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted/20 hover:[&::-webkit-scrollbar-thumb]:bg-muted/50 transition-colors flex items-center pb-2"
          >
            <div className="flex gap-8 px-2">
              {months.map((month) => (
                <div key={month.name} className="flex flex-col gap-2">
                  <div className="text-xs font-medium text-muted-foreground pl-1">{month.name}</div>
                  <div className="flex gap-1">
                    {month.grid.map((col, cIdx) => (
                      <div key={`col-${month.name}-${cIdx}`} className="flex flex-col gap-1">
                        {col.map((day, dIdx) => {
                          if (!day) return <div key={`empty-${month.name}-${cIdx}-${dIdx}`} className="w-5 h-5" />;

                          const dateStr = format(day, 'yyyy-MM-dd');
                          const activity = activityMap[dateStr];
                          const minutes = activity ? activity.totalMinutes : 0;
                          const sessions = activity ? activity.sessionCount : 0;

                          return (
                            <TooltipProvider key={dateStr} delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`w-5 h-5 rounded-md transition-all duration-200 cursor-default border border-transparent hover:border-border ${getActivityColor(minutes)}`}
                                  />
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <div className="text-xs">
                                    <div className="font-semibold text-foreground mb-0.5">{format(day, 'MMM d, yyyy')}</div>
                                    {minutes > 0 ? (
                                      <div className="space-y-0.5 text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                          <span className="text-primary">{formatMinutes(minutes)}</span> writing
                                        </div>
                                        <div>{sessions} {sessions === 1 ? 'session' : 'sessions'}</div>
                                      </div>
                                    ) : (
                                      <div className="text-muted-foreground">No activity</div>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-2 mr-4 text-xs text-muted-foreground flex-shrink-0">
            <span>Less</span>
            <div className="flex gap-1">
              <div className="w-4 h-4 bg-muted/20 rounded-[3px]" />
              <div className="w-4 h-4 bg-primary/20 rounded-[3px]" />
              <div className="w-4 h-4 bg-primary/40 rounded-[3px]" />
              <div className="w-4 h-4 bg-primary/60 rounded-[3px]" />
              <div className="w-4 h-4 bg-primary/80 rounded-[3px]" />
            </div>
            <span>More</span>
          </div>
        </div>
      )}
    </div>
  );
}